import { useCallback, useEffect, useRef } from 'react';
import { noop } from 'lodash';
import styled from 'styled-components';
import { theme, useMousePosition } from '@frameio/vapor';
import { AssetContextProvider } from '../../../shared';
import { AssetForTrail } from './TrailAsset';
import type { PointerCoords, TrailerRef } from './Trailer';
import { SMOOTHING_COEFF, Trailer } from './Trailer';

export const AssetTrail = ({
  trailIds,
  totalCount,
}: {
  /** ids to render  */
  trailIds: string[];
  totalCount: number;
}) => {
  const coords = useMousePosition();
  const refs = useRef<{ [key: number]: TrailerRef }>({});
  const rafToken = useRef<number | null>(null);
  const prevFrame = useRef<number>(performance.now());
  const target = useRef<PointerCoords>([0, 0]);

  const animateTrail = useCallback(
    (elapsed: number) => {
      const dt = elapsed - prevFrame.current;
      let targetCoords: PointerCoords = [target.current[0], target.current[1]];
      let requestNextFrame = false;

      Object.values(refs.current).forEach((ref, index) => {
        const current = ref.getTransform();
        const multiplier = Math.log(trailIds.length + 1 - index * 0.5);
        const smoothing = 1 - Math.exp(SMOOTHING_COEFF * dt * multiplier);

        const nextX = current[0] + (targetCoords[0] - current[0]) * smoothing;
        const nextY = current[1] + (targetCoords[1] - current[1]) * smoothing;

        let next: PointerCoords = [nextX, nextY];

        if (isCoordWithinThreshold(targetCoords, next)) {
          next = [targetCoords[0], targetCoords[1]];
        } else {
          requestNextFrame = true;
        }

        ref.setTransform(next);

        targetCoords = next;
      });

      prevFrame.current = elapsed;
      rafToken.current = null;

      if (requestNextFrame) {
        rafToken.current = requestAnimationFrame(animateTrail);
      }
    },
    [trailIds]
  );

  useEffect(() => {
    if (rafToken.current) {
      return noop;
    }

    target.current = coords;
    rafToken.current = requestAnimationFrame(animateTrail);

    return () => {
      if (typeof rafToken.current === 'number') {
        window.cancelAnimationFrame(rafToken.current);
      }
      rafToken.current = null;
    };
  }, [coords, animateTrail]);

  if (coords[0] < 0 || coords[1] < 0) {
    return undefined;
  }

  const trail = trailIds.map((id, index) => (
    <Trailer
      key={index}
      ref={(ref) => {
        if (ref) {
          refs.current[index] = ref;
        }
      }}
      coords={coords}
      index={index}
    >
      <AssetContextProvider assetType="file" id={id}>
        <AssetForTrail
          badgeCount={index === 0 && totalCount > 1 ? totalCount : undefined}
          id={id}
        />
      </AssetContextProvider>
    </Trailer>
  ));

  return <TrailContainer>{trail}</TrailContainer>;
};

const TrailContainer = styled.div`
  left: 0;
  position: fixed;
  top: 0;
  z-index: calc(${theme.zIndices.popover} + 1);
`;

const isCoordWithinThreshold = (
  first: PointerCoords,
  second: PointerCoords,
  threshold: number = 0.1
) =>
  Math.abs(first[0] - second[0]) < threshold &&
  Math.abs(first[1] - second[1]) < threshold;
